import type { EntityFormConfig } from '@components/integrated/entity-form';
import {
  createFormActions,
  createFormEndpoint,
  createFormCreateId,
  createFormEditId,
  createFormLayout,
  createFormWorkflow,
} from './formConfigHelpers';

/**
 * Returns an EntityFormConfig for Module create or edit mode.
 *
 * @param mode - 'create' or 'edit'
 * @param moduleSlug - Required for edit mode; the slug of the module being edited
 */
export function getModuleFormConfig(
  mode: 'create' | 'edit',
  moduleSlug?: string,
): EntityFormConfig {
  const isEdit = mode === 'edit';
  const basePath = '/api/admin/modules';
  const moduleEndpoint = moduleSlug ? `${basePath}/${moduleSlug}` : basePath;

  return {
    id: isEdit ? createFormEditId('module', moduleSlug ?? 'unknown') : createFormCreateId('module'),

    endpoint: createFormEndpoint(
      isEdit,
      basePath,
      isEdit ? moduleEndpoint : basePath,
      !!moduleSlug,
      ['slug', 'name', 'description', 'version', 'config', 'is_active'],
    ),

    fields: {
      slug: {
        type: 'text',
        label: 'Slug',
        placeholder: 'e.g. my-module',
        helperText: 'Lowercase letters, numbers, and hyphens only',
        section: 'basic',
        order: 1,
        validation: {
          required: true,
          max: { value: 100, message: 'Slug must be 100 characters or fewer' },
          pattern: {
            value: '^[a-z0-9-]+$',
            message: 'Only lowercase letters, numbers, and hyphens are allowed',
          },
        },
      },
      name: {
        type: 'text',
        label: 'Name',
        placeholder: 'e.g. Inventory Management',
        section: 'basic',
        order: 2,
        validation: {
          required: true,
          max: { value: 255, message: 'Name must be 255 characters or fewer' },
        },
      },
      description: {
        type: 'textarea',
        label: 'Description',
        placeholder: 'Describe what this module does...',
        section: 'basic',
        order: 3,
        options: {
          rows: 3,
          resize: 'vertical',
        },
      },
      version: {
        type: 'text',
        label: 'Version',
        placeholder: 'e.g. 1.0.0',
        section: 'basic',
        order: 4,
        validation: {
          max: { value: 50, message: 'Version must be 50 characters or fewer' },
        },
      },
      config: {
        type: 'textarea',
        label: 'Configuration (JSON)',
        helperText: 'JSON object for module configuration',
        placeholder: '{"feature_flags": {"advanced_tracking": true}}',
        section: 'settings',
        order: 5,
        options: {
          rows: 5,
          resize: 'vertical',
        },
      },
      is_active: {
        type: 'checkbox',
        label: 'Active',
        section: 'settings',
        order: 6,
        defaultValue: true,
      },
    },

    sections: [
      {
        id: 'basic',
        title: 'Basic Information',
        order: 1,
      },
      {
        id: 'settings',
        title: 'Settings',
        order: 2,
      },
    ],

    actions: createFormActions('Module', isEdit),

    workflow: createFormWorkflow('/admin/modules'),

    layout: createFormLayout(),
  };
}
